/**
 * Leaflink Global Constants.
 * NOTE: as per convention, all constants are written in UPPERCASE.
 */

/**
 * Key Codes
 */
export const KEY_CODES = Object.freeze({
  DOWN: 40,
  ENTER: 13,
  ESCAPE: 27,
  LEFT: 37,
  SPACE: 32,
  RIGHT: 39,
  UP: 38,
});

/**
 * Date Formats
 */
export const DATE_FORMATS = {
  DEFAULT: 'YYYY-MM-DD hh:mm A',
  MONTH_DAY_YEAR_AT_TIME: 'MMM DD, YYYY @ h:mmA',
  YYYY_MM_DD: 'YYYY-MM-DD',
  YYYY_MM_DD_DATE_FNS: 'yyyy-MM-dd',
  YYYY_MM_DD_TIME_DATE_FNS: 'yyyy-MM-dd hh:mm aa',
  TIME_DATE_FNS: 'hh:mm aa',
  YYYY_MM_DD_SLASHES: 'YYYY/MM/DD',
};

/**
 * Timeout for UI/UX effects
 */
export const UI_TIMEOUT = 3000;

/**
 * Debounce used for firing side-effects (tracking, etc)
 */
export const DEBOUNCE = Object.freeze({
  SLOW: 3000,
  MEDIUM: 1000,
  FAST: 200,
});

/**
 * Query selector string for getting all focusable elements
 */
export const FOCUS_ELEMENTS_SELECTOR =
  'button, [href]:not(use), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * License colors
 */
export const LICENSE_COLORS = Object.freeze({
  ADU: 'bg-blue-500',
  BULK: 'bg-ice-900',
  REC: 'bg-blue-500',
  MED: 'bg-yellow-500',
  COM: 'bg-royal-500',
  ANC: 'bg-orange-500',
  CAN: 'bg-green-500',
});

/**
 * CSS variable names
 */
export const CSS_VARS = {
  GRID_GUTTER: 'grid-gutter',
  TOP_HEADER_HEIGHT: 'height-topbar',
} as const;

/**
 * LeafLink app names
 */
export const APPS = {
  MARKETPLACE: 'marketplace',
  PAYMENTS: 'payments',
} as const;

/**
 * Change triggers for ListView
 */
export const LLLV_CHANGE_TRIGGERS = {
  APPLY: 'apply',
  LOAD: 'load',
  PAGE: 'page',
  RESET: 'reset',
  SEARCH: 'search',
  SORT: 'sort',
} as const;

/**
 * Key value pairs where the key is a currency code and the value is the
 * symbol for that code.
 */
export const CURRENCY_SYMBOL_MAP = Object.freeze({
  AED: 'د.إ',
  AFN: '؋',
  ALL: 'L',
  AMD: '֏',
  ANG: 'ƒ',
  AOA: 'Kz',
  ARS: '$',
  AUD: '$',
  AWG: 'ƒ',
  AZN: '₼',
  BAM: 'KM',
  BBD: '$',
  BDT: '৳',
  BGN: 'лв',
  BHD: '.د.ب',
  BIF: 'FBu',
  BMD: '$',
  BND: '$',
  BOB: '$b',
  BOV: 'BOV',
  BRL: 'R$',
  BSD: '$',
  BTC: '₿',
  BTN: 'Nu.',
  BWP: 'P',
  BYN: 'Br',
  BYR: 'Br',
  BZD: 'BZ$',
  CAD: '$',
  CDF: 'FC',
  CHE: 'CHE',
  CHF: 'CHF',
  CHW: 'CHW',
  CLF: 'CLF',
  CLP: '$',
  CNY: '¥',
  COP: '$',
  COU: 'COU',
  CRC: '₡',
  CUC: '$',
  CUP: '₱',
  CVE: '$',
  CZK: 'Kč',
  DJF: 'Fdj',
  DKK: 'kr',
  DOP: 'RD$',
  DZD: 'دج',
  EEK: 'kr',
  EGP: '£',
  ERN: 'Nfk',
  ETB: 'Br',
  ETH: 'Ξ',
  EUR: '€',
  FJD: '$',
  FKP: '£',
  GBP: '£',
  GEL: '₾',
  GGP: '£',
  GHC: '₵',
  GHS: 'GH₵',
  GIP: '£',
  GMD: 'D',
  GNF: 'FG',
  GTQ: 'Q',
  GYD: '$',
  HKD: '$',
  HNL: 'L',
  HRK: 'kn',
  HTG: 'G',
  HUF: 'Ft',
  IDR: 'Rp',
  ILS: '₪',
  IMP: '£',
  INR: '₹',
  IQD: 'ع.د',
  IRR: '﷼',
  ISK: 'kr',
  JEP: '£',
  JMD: 'J$',
  JOD: 'JD',
  JPY: '¥',
  KES: 'KSh',
  KGS: 'лв',
  KHR: '៛',
  KMF: 'CF',
  KPW: '₩',
  KRW: '₩',
  KWD: 'KD',
  KYD: '$',
  KZT: '₸',
  LAK: '₭',
  LBP: '£',
  LKR: '₨',
  LRD: '$',
  LSL: 'M',
  LTC: 'Ł',
  LTL: 'Lt',
  LVL: 'Ls',
  LYD: 'LD',
  MAD: 'MAD',
  MDL: 'lei',
  MGA: 'Ar',
  MKD: 'ден',
  MMK: 'K',
  MNT: '₮',
  MOP: 'MOP$',
  MRO: 'UM',
  MRU: 'UM',
  MUR: '₨',
  MVR: 'Rf',
  MWK: 'MK',
  MXN: '$',
  MXV: 'MXV',
  MYR: 'RM',
  MZN: 'MT',
  NAD: '$',
  NGN: '₦',
  NIO: 'C$',
  NOK: 'kr',
  NPR: '₨',
  NZD: '$',
  OMR: '﷼',
  PAB: 'B/.',
  PEN: 'S/.',
  PGK: 'K',
  PHP: '₱',
  PKR: '₨',
  PLN: 'zł',
  PYG: 'Gs',
  QAR: '﷼',
  RMB: '￥',
  RON: 'lei',
  RSD: 'Дин.',
  RUB: '₽',
  RWF: 'R₣',
  SAR: '﷼',
  SBD: '$',
  SCR: '₨',
  SDG: 'ج.س.',
  SEK: 'kr',
  SGD: 'S$',
  SHP: '£',
  SLL: 'Le',
  SOS: 'S',
  SRD: '$',
  SSP: '£',
  STD: 'Db',
  STN: 'Db',
  SVC: '$',
  SYP: '£',
  SZL: 'E',
  THB: '฿',
  TJS: 'SM',
  TMT: 'T',
  TND: 'د.ت',
  TOP: 'T$',
  TRL: '₤',
  TRY: '₺',
  TTD: 'TT$',
  TVD: '$',
  TWD: 'NT$',
  TZS: 'TSh',
  UAH: '₴',
  UGX: 'USh',
  USD: '$',
  UYI: 'UYI',
  UYU: '$U',
  UYW: 'UYW',
  UZS: 'лв',
  VEF: 'Bs',
  VES: 'Bs.S',
  VND: '₫',
  VUV: 'VT',
  WST: 'WS$',
  XAF: 'FCFA',
  XBT: 'Ƀ',
  XCD: '$',
  XOF: 'CFA',
  XPF: '₣',
  XSU: 'Sucre',
  XUA: 'XUA',
  YER: '﷼',
  ZAR: 'R',
  ZMW: 'ZK',
  ZWD: 'Z$',
  ZWL: '$',
});

/**
 * Image Provider URLs
 */

export const IMAGE_PROVIDER_URLS = {
  CLOUDINARY: 'https://res.cloudinary.com/leaflink/image/fetch',
} as const;

/**
 * The number of decimals to round a number
 */
export const DECIMAL_PRECISION = {
  CURRENCY: 2,
} as const;

export const LICENSE_CLASSIFICATIONS = Object.freeze({
  NONE: {
    id: 'None',
    displayName: '',
  },
  ADULT_USE: {
    id: 'Adult Use',
    displayName: 'Recreational',
  },
  MEDICAL: {
    id: 'Medical',
    displayName: 'Medical',
  },
  COMBINED: {
    id: 'Combined',
    displayName: 'Recreational',
  },
  CANNABIS: {
    id: 'Cannabis',
    displayName: 'Recreational',
  },
});

/**
 * Inbound Leads Statuses
 */
export const INBOUND_LEADS = Object.freeze({
  COMPLETE: {
    color: 'bg-purple-500',
    id: 'complete',
  },
  PENDING: {
    color: 'bg-orange-500',
    id: 'pending',
  },
  IGNORED: {
    color: 'bg-red-500',
    id: 'ignored',
  },
});

/**
 * Order statuses for both Buyer and Seller generated Orders.
 */
export const ORDER = Object.freeze({
  SUBMITTED: {
    color: 'ice-700',
    name: 'submitted',
    transitionName: 'submit',
  },
  ACCEPTED: {
    color: 'green',
    name: 'accepted',
    transitionName: 'accept',
  },
  FULFILLED: {
    color: 'royal',
    name: 'fulfilled',
    transitionName: 'fulfill',
  },
  SHIPPED: {
    color: 'blue',
    name: 'shipped',
    transitionName: 'ship',
  },
  COMPLETE: {
    color: 'purple',
    name: 'complete',
    transitionName: 'complete',
  },
  REJECTED: {
    color: 'red',
    name: 'rejected',
    transitionName: 'reject',
  },
  CANCELLED: {
    color: 'red',
    name: 'cancelled',
    transitionName: 'cancel',
  },
  DRAFT: {
    color: 'blue',
    name: 'draft',
    transitionName: '',
  },
  BACKORDER: {
    color: 'ice',
    name: 'backorder',
    transitionName: 'backorder',
  },
  COMBINED: {
    color: 'ice',
    name: 'combined',
    transitionName: 'combine',
  },
});

/**
 * Certified Seller Statuses
 */
export const SELLER_STATUSES = Object.freeze({
  eliteSeller: {
    color: 'royal',
    id: 'elite',
  },
  powerSeller: {
    color: 'green',
    id: 'power',
  },
});

// Tailwind is mobile first so there's no media query necessary for LL's sm
// breakpoint as it's effectively `@media (min-width: 0) { ... }`. Typically
// (in tailwind, bootstrap, etc) everything from 0 - sm "breakpoint" is
// considered XS. But for us, it's actually shifted up. We're not really
// defining "breakpoints" so much as we do ranges, so in Tailwind world, we
// don't need to define a `sm` breakpoint.
// See:
//   - https://tailwindcss.com/docs/screens#advanced-configuration
//   - https://tailwindcss.com/docs/responsive-design#targeting-a-breakpoint-range
export const SCREEN_SIZES = Object.freeze({
  md: '640px',
  // => @media (min-width: 640px) { ... }

  lg: '961px',
  // => @media (min-width: 961px) { ... }

  xl: '1321px',
  // => @media (min-width: 1321px) { ... }

  /**
   * Target only specific breakpoints.
   *
   * Note: These ONLY apply to the screen function in Tailwind v3, i.e. `@media screen('md:max-lg')`.
   * When using in a class like `class="lg:max-xl:bg-red"`, it will actually generate two
   * media queries nested inside each other like so: `@media (min-width: 961px) { @media (max-width: 1321px) { ... } }`.
   */

  // sm only - use `max-md` instead!

  // md only
  'md:max-lg': { min: '640px', max: '961px' },
  // => @media (min-width: 640px) and (max-width: 961px) { ... }

  // lg only
  'lg:max-xl': { min: '961px', max: '1321px' },
  // => @media (min-width: 961px) and (max-width: 1321px) { ... }

  /**
   * Max breakpoints.
   *
   * Important: Make sure to list max breakpoints in descending order so that they override each other as expected.
   */

  'max-xl': { raw: 'not all and (min-width: 1321px)' },
  // => @media (not all and (min-width: 1321px)) { ... }

  'max-lg': { raw: 'not all and (min-width: 969px)' },
  // => @media (not all and (min-width: 961px)) { ... }

  'max-md': { raw: 'not all and (min-width: 640px)' },
  // => @media (not all and (min-width: 640px)) { ... }
});
